import React, {
  useEffect, useState, useRef, useMemo, useCallback,
} from 'lib/react';
import { useLocation, useParams } from 'lib/router';
import {
  EVCircularProgress, EVTypography, EVGrid, EVButton,
  EVKeyboardArrowDown, EVKeyboardArrowUp,
} from 'lib/components';
import clsx from 'lib/clsx';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import useMediaQuery from 'hooks/useMediaQuery';
import getSitemapImageService from 'services/getSitemapImage.service';
import getSitemapBuildingDataService from 'services/getSitemapBuildingData.service';
import getReportDetailsService from 'services/getReportDetails.service';
import submitSitemapApprovalService from 'services/submitSitemapApproval.service';
import history from 'store/CreateHistory';
import { KEY, INITIALIZATION_VECTOR } from 'constants/AppConstants';
import { decodeAndDecrypt } from 'utils/app.utils';
import { logError } from 'utils/logger.utils';
import { useSelector } from 'lib/redux';
import { isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import { saveSitemapSubmittedPayload } from './sitemapSubmittedStorage';
import {
  formatSitemapOrderPlaced,
  formatSitemapReportAddress,
  getSitemapSubmittedPath,
  shouldRedirectFromSitemapApproval,
} from './sitemapStatus.utils';
import SitemapApprovalStyles from './SitemapApprovalStyles';
import SitemapApprovalSidebar from './components/SitemapApprovalSidebar.component';
import SitemapApprovalConfirmDialog from './components/SitemapApprovalConfirmDialog.component';
import SitemapApprovalErrorDialog from './components/SitemapApprovalErrorDialog.component';

const getFetchErrorDetail = (result) => {
  if (result.json?.errorCode) {
    return ` (${result.status}: ${result.json.errorCode} ${result.error || ''})`;
  }
  if (result.status) {
    return ` (${result.status})`;
  }
  return '';
};

const revokeObjectUrlIfPresent = (url) => {
  if (url) {
    URL.revokeObjectURL(url);
  }
};

const resolveImageResult = (imageResult) => {
  if (imageResult.ok) {
    return { imageUrl: imageResult.imageUrl, imageError: null };
  }
  return {
    imageUrl: null,
    imageError: `Unable to load sitemap image${getFetchErrorDetail(imageResult)}`,
  };
};

const applyBuildingResult = async ({
  buildingResult,
  encodedId,
  active,
  setBuildingData,
  setReportDetails,
  setBuildingDataError,
}) => {
  if (buildingResult.ok) {
    setBuildingData(buildingResult.json);
    const reportId = buildingResult.json?.reportId;
    if (!reportId) return null;
    const detailsResult = await getReportDetailsService(reportId);
    if (active && detailsResult.ok) {
      setReportDetails(detailsResult.json);
      return detailsResult.json;
    }
    return null;
  }

  // Building-data often fails after split; still load details for redirect/status.
  let details = null;
  try {
    const decryptedReportId = await decodeAndDecrypt(encodedId, KEY, INITIALIZATION_VECTOR);
    const detailsResult = await getReportDetailsService(decryptedReportId);
    if (active && detailsResult.ok) {
      setReportDetails(detailsResult.json);
      details = detailsResult.json;
    }
  } catch (error) {
    logError('SitemapApproval decrypt for status check failed', { error: error.message });
  }

  if (active) {
    setBuildingDataError(`Unable to load building data${getFetchErrorDetail(buildingResult)}`);
  }
  return details;
};

function SitemapApproval() {
  const classes = SitemapApprovalStyles();
  const location = useLocation();
  const { encryptedReportId: pathEncryptedReportId } = useParams();
  const queryParams = new URLSearchParams(location.search);
  // Primary: /sitemap-approval/:encryptedReportId — optional ?id= for email links
  const encryptedReportId = pathEncryptedReportId || queryParams.get('id');
  const isMobile = useMediaQuery(640);
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [buildingDataError, setBuildingDataError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [buildingData, setBuildingData] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const imageUrlRef = useRef(null);

  useEffect(() => {
    let active = true;

    const fetchPageData = async (encodedId) => {
      setLoading(true);
      setImageError(null);
      setBuildingDataError(null);
      setBuildingData(null);
      setReportDetails(null);
      setSelectedIds([]);

      if (imageUrlRef.current) {
        revokeObjectUrlIfPresent(imageUrlRef.current);
        imageUrlRef.current = null;
        setImageUrl(null);
      }

      const [imageResult, buildingResult] = await Promise.all([
        getSitemapImageService(encodedId),
        getSitemapBuildingDataService(encodedId),
      ]);

      if (!active) {
        revokeObjectUrlIfPresent(imageResult.ok ? imageResult.imageUrl : null);
        return;
      }

      const imageOutcome = resolveImageResult(imageResult);
      if (imageOutcome.imageUrl) {
        imageUrlRef.current = imageOutcome.imageUrl;
        setImageUrl(imageOutcome.imageUrl);
      } else {
        setImageError(imageOutcome.imageError);
      }
      const details = await applyBuildingResult({
        buildingResult,
        encodedId,
        active,
        setBuildingData,
        setReportDetails,
        setBuildingDataError,
      });

      if (!active) return;
      if (details && shouldRedirectFromSitemapApproval(details)) {
        history.replace(getSitemapSubmittedPath(encodedId));
        return;
      }

      setLoading(false);
    };

    if (encryptedReportId) {
      fetchPageData(encryptedReportId);
    } else {
      setImageError('Missing encrypted report ID');
      setBuildingDataError('Missing encrypted report ID');
      setLoading(false);
    }

    return () => {
      active = false;
      if (imageUrlRef.current) {
        revokeObjectUrlIfPresent(imageUrlRef.current);
        imageUrlRef.current = null;
      }
    };
  }, [encryptedReportId]);

  const structureAddress = useMemo(
    () => formatSitemapReportAddress(reportDetails?.reportAddress, {
      fallback: 'Address unavailable',
    }),
    [reportDetails],
  );

  const totalCount = buildingData?.totalNumbersPlotted ?? 0;

  const allStructures = useMemo(() => (
    Array.from({ length: totalCount }, (_, i) => ({
      id: i + 1,
      address: structureAddress,
    }))
  ), [totalCount, structureAddress]);

  const allSelected = totalCount > 0 && selectedIds.length === totalCount;

  const handleSelectAll = useCallback((_, checked) => {
    if (checked) {
      setSelectedIds(allStructures.map((s) => s.id));
    } else {
      setSelectedIds([]);
    }
  }, [allStructures]);

  const handleToggleStructure = useCallback((id) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedIds.length === 0 || submitting) return;
    setErrorOpen(false);
    setConfirmOpen(true);
  }, [selectedIds, submitting]);

  const handleCancelConfirm = useCallback(() => {
    if (submitting) return;
    setConfirmOpen(false);
  }, [submitting]);

  const handleCloseError = useCallback(() => {
    setErrorOpen(false);
  }, []);

  const handleTryAgain = useCallback(() => {
    setErrorOpen(false);
    setConfirmOpen(true);
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    if (selectedIds.length === 0 || submitting) return;

    setSubmitting(true);

    const buildingIds = [...selectedIds].sort((a, b) => a - b);
    const result = await submitSitemapApprovalService({
      encryptedReportId,
      buildingIds,
      imagelink: '',
    });

    if (!result.ok) {
      setSubmitting(false);
      setConfirmOpen(false);
      setErrorOpen(true);
      return;
    }

    saveSitemapSubmittedPayload({
      encryptedReportId,
      reportId: buildingData?.reportId ?? reportDetails?.reportId ?? null,
      selectedBuildingIds: buildingIds,
      address: structureAddress,
      orderPlacedDate: reportDetails?.orderPlacedDate || null,
      productType: reportDetails?.reportType || null,
      isSubscription: Boolean(isSubscriptionDashboardV2Enabled),
      splitResponse: result.json,
    });
    history.push(getSitemapSubmittedPath(encryptedReportId));
  }, [
    selectedIds,
    submitting,
    encryptedReportId,
    buildingData,
    reportDetails,
    structureAddress,
    isSubscriptionDashboardV2Enabled,
  ]);

  const reportIdDisplay = buildingData?.reportId ?? reportDetails?.reportId ?? '—';
  const orderPlacedDisplay = formatSitemapOrderPlaced(reportDetails?.orderPlacedDate, { includeTime: true });
  const orderPlacedDateOnly = formatSitemapOrderPlaced(reportDetails?.orderPlacedDate);

  if (loading) {
    return (
      <EVGrid className={classes.loaderContainer}>
        <EVCircularProgress size={40} />
      </EVGrid>
    );
  }

  const metadata = (
    <EVGrid className={classes.metadataRow}>
      <EVTypography className={classes.metadataItem} component="span">
        <span className={classes.metadataLabel}>Report #: </span>
        {reportIdDisplay}
      </EVTypography>
      <EVTypography className={classes.metadataDivider} component="span">|</EVTypography>
      <EVTypography className={classes.metadataItem} component="span">
        <span className={classes.metadataLabel}>Address: </span>
        {structureAddress}
      </EVTypography>
      <EVTypography className={classes.metadataDivider} component="span">|</EVTypography>
      <EVTypography className={classes.metadataItem} component="span">
        <span className={classes.metadataLabel}>Order placed on: </span>
        {orderPlacedDisplay}
      </EVTypography>
    </EVGrid>
  );

  const sidebarProps = {
    classes,
    totalCount,
    structures: allStructures,
    selectedIds,
    onToggleStructure: handleToggleStructure,
    onSubmit: handleSubmit,
    submitting,
  };

  return (
    <EVGrid className={classes.page}>
      {/* Title/metadata live in the same relative page as the drawer (Order pattern) */}
      <EVTypography className={classes.title}>
        Select which structures you want measured
      </EVTypography>
      <EVTypography className={classes.subtitle}>
        Use the checkboxes to select every structure you need measured from your order.
      </EVTypography>
      {metadata}

      <EVGrid className={classes.contentRow}>
        <EVGrid className={classes.mainColumn}>
          <EVGrid className={classes.selectAllRow}>
            <LabelledCheckbox
              checkboxId="sitemap-approval-select-all"
              checkboxLabel="Select all structures"
              checked={allSelected}
              checkboxValue={allSelected}
              handleCheckboxSelectChange={handleSelectAll}
              disabled={totalCount === 0}
              indeterminate={selectedIds.length > 0 && !allSelected}
            />
          </EVGrid>

          <EVGrid className={classes.imageContainer}>
            {imageError && (
              <EVTypography className={classes.imageError}>{imageError}</EVTypography>
            )}
            {imageUrl && (
              <img src={imageUrl} alt="Sitemap" className={classes.image} />
            )}
          </EVGrid>

          {buildingDataError && (
            <EVTypography className={classes.errorText}>{buildingDataError}</EVTypography>
          )}
        </EVGrid>

        {/* Desktop sidebar */}
        <EVGrid className={classes.sidebarDesktop}>
          <SitemapApprovalSidebar {...sidebarProps} />
        </EVGrid>
      </EVGrid>

      {/* Mobile bottom drawer — absolute inside page (below sticky header z-index 200) */}
      {isMobile && (
        <EVGrid
          className={clsx(
            classes.sidebarMobile,
            drawerOpen ? classes.sidebarMobileOpen : classes.sidebarMobileClosed,
          )}
        >
          <EVButton
            fullWidth
            className={classes.toggleDrawerButton}
            onClick={() => setDrawerOpen((open) => !open)}
          >
            {drawerOpen ? (
              <>
                Map
                <EVKeyboardArrowDown className={classes.toggleDrawerArrow} />
              </>
            ) : (
              <>
                Selected Structures
                <EVKeyboardArrowUp className={classes.toggleDrawerArrow} />
              </>
            )}
          </EVButton>
          {drawerOpen && (
            <SitemapApprovalSidebar {...sidebarProps} />
          )}
        </EVGrid>
      )}

      <SitemapApprovalConfirmDialog
        open={confirmOpen}
        reportId={reportIdDisplay}
        address={structureAddress}
        orderPlacedDisplay={orderPlacedDateOnly}
        selectedCount={selectedIds.length}
        totalCount={totalCount}
        submitting={submitting}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirmSubmit}
      />
      <SitemapApprovalErrorDialog
        open={errorOpen}
        onClose={handleCloseError}
        onTryAgain={handleTryAgain}
      />
    </EVGrid>
  );
}

export default SitemapApproval;
